import { useEffect, useRef, useState } from 'react';

import { normalizeBithumbTicker } from '../lib/normalize';
import { bithumbSocketManager } from '../lib/bithumbSocket';
import { useRealtimeGaveUpToast } from './useRealtimeGaveUpToast';

import type { NormalizedTicker } from '../types/exchange';

interface UseBithumbWebSocketProps {
  markets: string[];
  onTickerUpdate: (ticker: NormalizedTicker) => void;
  enabled?: boolean;
}

export const parseBithumbMessage = async (data: Blob): Promise<NormalizedTicker | null> => {
  try {
    const text = await data.text();
    const parsed = JSON.parse(text);

    if (parsed.type !== 'ticker') {
      return null;
    }

    // 업비트 호환 형식 (ws-api.bithumb.com — snake_case, 필드가 루트에 있음)
    if (parsed.code) {
      return normalizeBithumbTicker({
        code: parsed.code,
        trade_price: parsed.trade_price,
        opening_price: parsed.opening_price,
        high_price: parsed.high_price,
        low_price: parsed.low_price,
        prev_closing_price: parsed.prev_closing_price,
        change: parsed.change,
        change_price: parsed.change_price,
        change_rate: parsed.change_rate,
        signed_change_price: parsed.signed_change_price,
        signed_change_rate: parsed.signed_change_rate,
        acc_trade_volume_24h: parsed.acc_trade_volume_24h,
        acc_trade_price_24h: parsed.acc_trade_price_24h,
        highest_52_week_price: parsed.highest_52_week_price,
        highest_52_week_date: parsed.highest_52_week_date,
        lowest_52_week_price: parsed.lowest_52_week_price,
        lowest_52_week_date: parsed.lowest_52_week_date,
      });
    }

    // 빗썸 공식 형식 (pubwss.bithumb.com — camelCase, content 내부)
    const c = parsed.content;
    if (!c) return null;

    const chgAmt = Number(c.chgAmt ?? 0);
    const change = chgAmt > 0 ? 'RISE' : chgAmt < 0 ? 'FALL' : 'EVEN';
    const parts = (c.symbol ?? '').split('_');
    const market = parts.length === 2 ? `${parts[1]}-${parts[0]}` : c.symbol;

    return normalizeBithumbTicker({
      code: market,
      trade_price: Number(c.closePrice),
      opening_price: Number(c.openPrice),
      high_price: Number(c.highPrice),
      low_price: Number(c.lowPrice),
      prev_closing_price: Number(c.prevClosePrice),
      change,
      change_price: Math.abs(chgAmt),
      change_rate: Number(c.chgRate ?? 0),
      signed_change_price: chgAmt,
      signed_change_rate: Number(c.chgRate ?? 0),
      acc_trade_volume_24h: Number(c.volume ?? 0),
      acc_trade_price_24h: Number(c.value ?? 0),
    });
  } catch (error) {
    // 🔴 raw error를 삼키지 않는다(TRAD-3045 조사에서 지적된 패턴).
    console.error('[useBithumbWebSocket] ticker parse failed', error);
    return null;
  }
};

export const useBithumbWebSocket = ({ markets, onTickerUpdate, enabled = true }: UseBithumbWebSocketProps) => {
  const onTickerUpdateRef = useRef(onTickerUpdate);
  const [isConnected, setIsConnected] = useState(false);

  useRealtimeGaveUpToast(bithumbSocketManager, enabled && markets.length > 0);

  useEffect(() => {
    onTickerUpdateRef.current = onTickerUpdate;
  }, [onTickerUpdate]);

  useEffect(() => {
    if (!enabled || markets.length === 0) return;

    const handleMessage = async (data: string | ArrayBuffer | Blob) => {
      const ticker = await parseBithumbMessage(data as Blob);
      if (ticker) {
        onTickerUpdateRef.current(ticker);
      }
    };

    const unsubscribe = bithumbSocketManager.subscribe('ticker', markets, handleMessage);
    const unlistenConnection = bithumbSocketManager.onConnectionChange(setIsConnected);
    setIsConnected(bithumbSocketManager.connected);

    return () => {
      unsubscribe();
      unlistenConnection();
    };
  }, [enabled, markets.join(',')]);

  return { isConnected };
};
